<template>
  <v-dialog v-model="dialog" persistent max-width="620">
    <v-card class="image-picker-card">
      <input
        type="file"
        ref="image"
        style="display: none"
        accept="image/*"
        @change="onFilePicked"
      />
      <div class="image-picker-header">
        <div>
          <div class="image-picker-title">{{ entityLabel }} Images</div>
          <div class="image-picker-subtitle">
            Search for an image, upload a file, or enter a URL.
          </div>
        </div>
        <v-btn icon small class="image-picker-close" @click="$emit('close')">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="image-picker-body">
        <div class="image-source-card">
          <div class="image-source-icon">
            <v-icon color="primary">mdi-image-search-outline</v-icon>
          </div>
          <div class="image-source-copy">
            <div class="image-source-title">Find {{ entityLabel.toLowerCase() }} images</div>
            <div class="image-source-description">
              Search using “{{ name || 'product name' }}” and choose from up to five results.
            </div>
          </div>
          <v-btn
            rounded
            depressed
            color="primary"
            :loading="searchLoading"
            :disabled="!name"
            @click="searchImages"
          >
            Search
          </v-btn>
        </div>

        <div v-if="images.length" class="image-results">
          <button
            v-for="(image, index) in images"
            :key="`${image}-${index}`"
            type="button"
            class="image-result-card"
            :disabled="selectionLoading"
            @click="select(image)"
          >
            <v-img :src="image" :lazy-src="image" aspect-ratio="1" contain>
              <template v-slot:placeholder>
                <div class="image-result-loading">
                  <v-progress-circular indeterminate size="24" color="primary" />
                </div>
              </template>
            </v-img>
          </button>
        </div>

        <div class="image-picker-divider"><span>or add your own</span></div>

        <v-btn
          :loading="loading"
          block
          rounded
          outlined
          color="primary"
          @click="pickFile"
        >
          <v-icon left small>mdi-upload</v-icon>
          Upload From Computer
        </v-btn>

        <div class="image-url-row mt-4">
          <div class="image-url-field">
            <v-text-field
              v-model="loadUrl"
              outlined
              clearable
              @keyup.enter="loadImage"
              ref="imageUrl"
              :error-messages="urlError"
              :hide-details="urlError.length === 0"
              @focus="$event.target.select()"
              dense
              label="Enter URL"
            />
          </div>
          <v-btn
            rounded
            depressed
            color="primary"
            class="image-url-button"
            @click="loadImage"
          >
            Add URL
          </v-btn>
        </div>
      </v-card-text>

      <div class="image-picker-footer">
        <div v-if="searchSource" class="image-search-source">
          <v-icon small>
            {{ searchSource === 'cache' || searchSource === 'database' ? 'mdi-database' : 'mdi-web' }}
          </v-icon>
          {{ searchSource === 'cache' || searchSource === 'database' ? 'Saved results' : 'Web results' }}
        </div>
        <v-progress-circular
          v-if="selectionLoading"
          class="ml-3"
          indeterminate
          size="20"
          width="2"
          color="primary"
        />
        <v-spacer></v-spacer>
        <v-btn rounded depressed color="primary" @click="$emit('close')">
          Done
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
const { validationMixin } = require("vuelidate");
const { url, required } = require("vuelidate/lib/validators");
import attachmentService from "@/modules/Attachment/service.js";
import productService from "@/modules/Product/service.js";

export default {
  props: {
    name: String,
    brand: String,
    upc: String,
    sku: String,
    imageResult: Object,
    showDialog: Boolean,
    field: String,
    autoSearch: Boolean,
  },
  data() {
    return {
      loading: false,
      searchLoading: false,
      selectionLoading: false,
      searchSource: null,
      loadUrl: null,
      dialog: false,
      images: [],
      imageName: "",
      imageUrl: "",
      imageFile: "",
      candidateByPreviewUrl: {},
    };
  },
  mixins: [validationMixin],
  validations: {
    loadUrl: {
      required,
      url,
    },
  },
  watch: {
    showDialog(val) {
      this.dialog = val;
    },
    dialog(val) {
      if (val) {
        this.resetPickerState();
        if (this.imageResult && Array.isArray(this.imageResult.candidates)) {
          this.setSearchResults(this.imageResult);
        } else if (this.autoSearch && this.name) {
          this.searchImages();
        }
        setTimeout(() => {
          if (this.$refs.imageUrl) this.$refs.imageUrl.focus();
        }, 100);
      }
    },
    name() {
      this.resetPickerState();
    },
  },
  computed: {
    entityLabel() {
      return this.field === "category" ? "Category" : "Product";
    },
    urlError() {
      const errors = [];
      if (!this.$v.loadUrl.$dirty) return errors;
      if (!this.$v.loadUrl.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      if (!this.$v.loadUrl.url) errors.push("Valid URL");
      return errors;
    },
  },
  methods: {
    resetPickerState() {
      this.images = [];
      this.candidateByPreviewUrl = {};
      this.searchSource = null;
      this.loadUrl = null;
      this.$v.$reset();
    },
    setSearchResults(result) {
      if (!result) return;

      const candidates = Array.isArray(result.candidates)
        ? result.candidates
        : [
            result.imgUrl1,
            result.imgUrl2,
            result.imgUrl3,
            result.imgUrl4,
            result.imgUrl5,
          ].filter(Boolean).map((imageUrl) => ({ url: imageUrl }));

      this.images = [];
      this.candidateByPreviewUrl = {};
      candidates.forEach((candidate) => {
        const previewUrl = candidate.thumbnailUrl || candidate.url;
        if (!previewUrl || !candidate.url) return;
        this.images.push(previewUrl);
        this.$set(this.candidateByPreviewUrl, previewUrl, {
          url: candidate.url,
          sourcePage: candidate.sourcePage || null,
          owned: false,
          imageSource: "web-search",
        });
      });
      this.searchSource = result.source || result.searchType || null;
    },
    async searchImages() {
      if (!this.name) return;

      this.searchLoading = true;
      try {
        const response = await productService.searchProductImages({
          q: this.name,
          brand: this.brand,
          upc: this.upc,
          sku: this.sku,
        });
        const result = response && response.data ? response.data : response;
        this.setSearchResults(result);

        if (!this.images.length) {
          this.$swal({
            icon: "info",
            title: "No images found",
            text: "Try a more specific product name or upload an image.",
          });
        }
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "Image search unavailable",
          text: "Please upload a file or enter an image URL.",
        });
      } finally {
        this.searchLoading = false;
      }
    },
    pickFile() {
      this.$refs.image.click();
    },

    optimizeImage(file, maxSize = 1000, quality = 0.78) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const img = new Image();

        reader.onload = (event) => {
          img.onload = () => {
            let width = img.width;
            let height = img.height;

            if (width > height && width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            } else if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);

            const base64 = canvas.toDataURL("image/jpeg", quality);
            resolve(base64);
          };

          img.onerror = reject;
          img.src = event.target.result;
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    async onFilePicked(e) {
      const files = e.target.files;

      if (!files || !files[0]) {
        this.imageName = "";
        this.imageFile = "";
        this.imageUrl = "";
        return;
      }

      const file = files[0];

      this.imageName = file.name;

      if (this.imageName.lastIndexOf(".") <= 0) {
        return;
      }

      this.loading = true;

      try {
        const optimizedImage = await this.optimizeImage(file, 1000, 0.78);
        const thumbnailImage = await this.optimizeImage(file, 240, 0.72);

        this.imageUrl = optimizedImage;
        this.imageFile = file;

        const result = await attachmentService.upload({
          base64: optimizedImage,
          thumbnailBase64: thumbnailImage,
          field: this.field,
          name: this.imageName,
        });

        if (result.status) {
          const imageUrl =
            result.data && result.data.imageUrl
              ? result.data.imageUrl
              : result.data;

          const thumbnailUrl =
            result.data && result.data.thumbnailUrl
              ? result.data.thumbnailUrl
              : null;

          const previewUrl = thumbnailUrl || imageUrl;
          this.images.push(previewUrl);

          // Keep the lightweight thumbnail in the picker, but never promote it
          // to the product's main image when the user selects it.
          this.$set(this.candidateByPreviewUrl, previewUrl, {
            url: imageUrl,
            thumbnailUrl,
            sourcePage: null,
            owned: true,
            imageSource: "upload",
          });

          this.$emit("uploaded", result.data);
          this.loading = false;

          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "success",
              title: "Image is Uploaded!",
            });
        }
      } catch (error) {
        console.log("error", error);
        this.loading = false;
      }

      e.target.value = "";
    },

    loadImage() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      this.images.push(this.loadUrl);
      this.loadUrl = null;
      this.$v.$reset();

      setTimeout(() => {
        this.$refs.imageUrl.focus();
      }, 100);
    },

    async select(val) {
      const candidate = this.candidateByPreviewUrl[val] || {
        url: val,
        sourcePage: null,
        owned: false,
        imageSource: "url",
      };

      if (candidate.owned) {
        this.$emit("uploaded", {
          imageUrl: candidate.url,
          thumbnailUrl: candidate.thumbnailUrl || candidate.url,
          imageSource: "upload",
        });
        return;
      }

      this.selectionLoading = true;
      try {
        const response = await productService.importProductImage({
          identity: {
            name: this.name,
            brand: this.brand,
            upc: this.upc,
            sku: this.sku,
          },
          sourceUrl: candidate.url,
          sourcePage: candidate.sourcePage,
          visibility: "shared_pending",
        });
        const imported = response && response.data ? response.data : response;
        if (!imported || !imported.imageUrl) {
          throw new Error("Image import returned no image URL");
        }
        this.$emit("uploaded", {
          imageUrl: imported.imageUrl,
          thumbnailUrl: imported.thumbnailUrl || imported.imageUrl,
          imageSource: candidate.imageSource,
        });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "Could not save image",
          text: "Please try another image or upload a file.",
        });
      } finally {
        this.selectionLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.image-picker-card {
  overflow: hidden;
  border-radius: 20px !important;
}

.image-picker-header,
.image-picker-footer {
  display: flex;
  align-items: center;
  padding: 20px 24px;
}

.image-picker-header {
  justify-content: space-between;
  border-bottom: 1px solid #e3e8ef;
}

.image-picker-title {
  color: #101828;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
}

.image-picker-subtitle,
.image-source-description,
.image-search-source {
  color: #71829d;
}

.image-picker-subtitle {
  margin-top: 4px;
  font-size: 14px;
}

.image-picker-close {
  background: #f4f6f8;
}

.image-picker-body {
  padding: 24px !important;
}

.image-source-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid #e3e8ef;
  border-radius: 16px;
  background: #f8fafc;
}

.image-source-icon {
  display: flex;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fff4c7;
}

.image-source-copy {
  min-width: 0;
  flex: 1;
}

.image-source-title {
  color: #101828;
  font-size: 16px;
  font-weight: 700;
}

.image-source-description {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.4;
}

.image-results {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
}

.image-result-card {
  overflow: hidden;
  padding: 6px;
  border: 2px solid transparent;
  border-radius: 12px;
  background: #f4f6f8;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;
}

.image-result-card:hover,
.image-result-card:focus-visible {
  border-color: var(--v-primary-base);
  outline: none;
  transform: translateY(-2px);
}

.image-result-loading {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}

.image-picker-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 22px 0;
  color: #8a97aa;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.image-picker-divider::before,
.image-picker-divider::after {
  height: 1px;
  flex: 1;
  background: #e3e8ef;
  content: "";
}

.image-url-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.image-url-field {
  min-width: 0;
  flex: 1;
}

.image-url-button {
  min-height: 40px;
}

.image-picker-footer {
  border-top: 1px solid #e3e8ef;
}

.image-search-source {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

@media (max-width: 600px) {
  .image-picker-header,
  .image-picker-footer {
    padding: 16px;
  }

  .image-picker-body {
    padding: 16px !important;
  }

  .image-source-card {
    flex-wrap: wrap;
  }

  .image-source-card > .v-btn {
    width: 100%;
  }

  .image-results {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .image-url-row {
    flex-direction: column;
  }

  .image-url-field,
  .image-url-button {
    width: 100%;
  }
}
</style>
